import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import {
  MailIcon,
  ChatIcon,
  UserIcon,
  InboxIcon,
  PlusSmIcon,
  CalendarIcon,
} from '@heroicons/react/outline';

import { Combobox, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/outline';

import {
  Feature,
  PatientDataPermission,
  ClientSessionReviewStatus,
  useGetClientSessionsCountQuery,
  useUninvitedEventInviteesQuery,
  useUpcomingScheduledEventsQuery,
  useMeProviderProgramTagsWithCountQuery,
  useProviderUserSignableFormsCountQuery,
  useCalendlyTeamProviderUsersQuery,
} from '../../../generated/graphql';

import { useAuth } from '../../../contexts/AuthContext';
import { useFeatureFlags } from '../../../contexts/FeatureFlagContext';
import { useGlobalModalsContext } from '../../../contexts/GlobalModalsContext';
import { useStripeIntegration } from '../../hooks/useStripeIntegration';
import Alert from '../../components/Alert';

import {
  HAS_DISMISSED_PAYMENTS_SETUP,
  HAS_DISMISSED_SCHEDULE_SETUP,
  HAS_DISMISSED_CALENDAR_SETUP,
  HAS_DISMISSED_STOREFRONT_SETUP,
  HAS_DISMISSED_ADD_CALENDLY_CLIENTS_SETUP,
  HAS_DISMISSED_APPEARANCE_SETUP,
} from '../../lib/storage';

import { DEFAULT_COLORS } from '../../lib/colors';
import { AnalyticsPage } from '../../../lib/analytics';
import { defaultTransitionProps } from '../../lib/animation';

import { isProviderUserOwnerOrAdminAuthorized } from '../../../lib/auth';

import BanknotesIcon from '../../svgs/BanknotesIcon';
import ActivityForm from '../../svgs/ActivityForm';
import HomeIllustration from '../../svgs/HomeIllustration';
import BellBadgeCalendar from '../../svgs/BellBadgeCalendar';
import IllustrationFaces from '../../svgs/IllustrationFaces';
import IllustrationCalendar from '../../svgs/IllustrationCalendar';
import CircleGreenCheck from '../../svgs/CircleGreenCheck';
import Spinner from '../../svgs/Spinner';

import { PatientRow } from '../../types/PatientManagement';

import useIncrementNowDate from '../../hooks/useIncrementNowDate';
import useLocalStorageValue from '../../hooks/useLocalStorageValue';

import Button from '../../components/Button';
import UpcomingEvent from '../../components/UpcomingEvent';
import AddPDFModal from '../../components/Modals/AddPDFModal';
import AddLinkModal from '../../components/Modals/AddLinkModal';
import AddToLibraryMenu from '../../components/AddToLibraryMenu';
import AddAudioModal from '../../components/Modals/AddAudioModal';
import AddImageModal from '../../components/Modals/AddImageModal';
import PageContainer from '../../components/Containers/PageContainer';
import AddPatientsModal from '../../components/Modals/AddPatientsModal';
import AddOrEditTextModal from '../../components/Modals/AddOrEditTextModal';
import Avatar from '../../components/Avatar';

import InboxView from './InboxView';
import { QuickActionProps } from './QuickAction';
import SelectPatientModal from './SelectPatientModal';
import UnsignedFormsView from './UnsignedFormsView';
import IllustrationBanknotes from '../../svgs/IllustrationBanknotes';
import IllustrationPencil from '../../svgs/IllustrationPencil';
import IllustrationBook from '../../svgs/IllustrationBook';
import IllustrationSwirls from '../../svgs/ServiceIcons/IllustrationSwirls';

import { SCHEDULE_MAIL_PATH } from '../ScheduleMail/types';

enum ViewMode {
  LatestActivity = 'LatestActivity',
  UnsignedForms = 'UnsignedForms',
}

const Home = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    authedProviderUser,
    willHitClientLimit,
    refreshAuthedProviderUserNotifications,
  } = useAuth();

  const now = useIncrementNowDate();
  const { isFeatureEnabled } = useFeatureFlags();
  const { showUpgradeModal } = useGlobalModalsContext();
  const isCalendarSyncEnabled = isFeatureEnabled(Feature.CalendarSync);
  const isClientSessionsEnabled = isFeatureEnabled(Feature.ClientSessions);
  const isMailSequencesEnabled = isFeatureEnabled(Feature.MailSequences);

  // Initialize view mode from URL parameter, default to LatestActivity
  const initialViewMode =
    searchParams.get('view') === 'unsigned-forms'
      ? ViewMode.UnsignedForms
      : ViewMode.LatestActivity;
  const [viewMode, setViewMode] = useState<ViewMode>(initialViewMode);

  const setViewModeAndParams = (mode: ViewMode) => {
    setViewMode(mode);
    const next = new URLSearchParams(searchParams);
    if (mode === ViewMode.UnsignedForms) {
      next.set('view', 'unsigned-forms');
    } else {
      next.delete('view');
    }
    setSearchParams(next);
  };

  // Update view mode when URL parameter changes
  useEffect(() => {
    const viewParam = searchParams.get('view');
    if (viewParam === 'unsigned-forms') {
      setViewMode(ViewMode.UnsignedForms);
    } else if (viewParam === null || viewParam === 'latest-activity') {
      setViewMode(ViewMode.LatestActivity);
    }
  }, [searchParams]);

  // Scroll to top when component mounts or view mode changes
  useEffect(() => {
    window.scrollTo(0, 0);
    const timeout1 = setTimeout(() => window.scrollTo(0, 0), 100);
    const timeout2 = setTimeout(() => window.scrollTo(0, 0), 300);
    const timeout3 = setTimeout(() => window.scrollTo(0, 0), 500);

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
    };
  }, [viewMode, searchParams]);

  const isOwnerOrAdmin =
    isProviderUserOwnerOrAdminAuthorized(authedProviderUser);

  const hasNylasIntegration = Boolean(authedProviderUser?.hasNylasIntegration);

  const calendlyIntegration = authedProviderUser?.provider.calendlyIntegration;
  const hasCalendlyIntegration = Boolean(calendlyIntegration);
  const isCalendlyEnabled = calendlyIntegration?.isEnabled ?? true;
  const showCalendlyReconnectWarning = Boolean(
    calendlyIntegration && !isCalendlyEnabled,
  );

  const { canUseStripe } = useStripeIntegration();

  const [selectedProviderUserId, setSelectedProviderUserId] = useState<
    string | null
  >(null);

  // Fetch team provider users (only for Owner/Admin)
  const { data: teamProviderUsersData, loading: teamProviderUsersLoading } =
    useCalendlyTeamProviderUsersQuery({
      skip: !isOwnerOrAdmin || !hasCalendlyIntegration || !isCalendlyEnabled,
    });

  const teamProviderUsers =
    teamProviderUsersData?.calendlyTeamProviderUsers || [];

  // Check if current user is in the Calendly team (for dropdown visibility)
  const currentUserInTeam = teamProviderUsers.find(
    (user) => user.id === authedProviderUser?.id,
  );
  const isCurrentUserInCalendlyTeam = Boolean(currentUserInTeam);

  // Set default to current user if not set
  useEffect(() => {
    if (
      !selectedProviderUserId &&
      authedProviderUser?.id &&
      teamProviderUsers.length > 0
    ) {
      // Check if current user is in the team list, otherwise use first available
      setSelectedProviderUserId(
        currentUserInTeam?.id || teamProviderUsers[0]?.id || null,
      );
    }
  }, [
    selectedProviderUserId,
    authedProviderUser?.id,
    teamProviderUsers,
    currentUserInTeam,
  ]);

  const isWaitingForTeamMemberSelection =
    isOwnerOrAdmin &&
    hasCalendlyIntegration &&
    isCalendlyEnabled &&
    (teamProviderUsersLoading ||
      (teamProviderUsers.length > 0 && !selectedProviderUserId));

  const { data: upcomingEventsData, loading: upcomingEventsLoading } =
    useUpcomingScheduledEventsQuery({
      variables: {
        input: {
          count: 5,
          minStartTime: now,
          isCanceled: false,
          providerUserId: selectedProviderUserId || undefined,
        },
      },
      skip:
        !hasCalendlyIntegration ||
        !isCalendlyEnabled ||
        isWaitingForTeamMemberSelection,
      notifyOnNetworkStatusChange: true,
    });

  const upcomingEvents =
    hasCalendlyIntegration && isCalendlyEnabled
      ? upcomingEventsData?.scheduledEvents
      : [];

  const hasUpcomingEvents = Boolean(upcomingEvents?.length);

  const isUpNextLoading =
    isWaitingForTeamMemberSelection || upcomingEventsLoading;

  const { data: uninvitedEventInviteesData } = useUninvitedEventInviteesQuery({
    skip: !hasCalendlyIntegration || !isCalendlyEnabled,
  });

  const { data: signableFormsCountData, loading: isLoadingSignableFormsCount } =
    useProviderUserSignableFormsCountQuery();
  const unsignedFormsCount = isLoadingSignableFormsCount
    ? 0
    : signableFormsCountData?.providerUserSignableFormsCount ?? 0;

  const { data: pendingClientSessionsCountData } =
    useGetClientSessionsCountQuery({
      variables: {
        input: { reviewStatus: ClientSessionReviewStatus.PendingReview },
      },
    });

  const pendingClientSessionsCount =
    pendingClientSessionsCountData?.clientSessionsCount ?? 0;

  const hasPendingClientSessions =
    isClientSessionsEnabled && pendingClientSessionsCount > 0;

  const uniqueInvitees: PatientRow[] | undefined =
    uninvitedEventInviteesData?.uninvitedEventInvitees.map((invitee) => ({
      name: `${invitee.firstName} ${invitee.lastName}`,
      email: invitee.email,
    }));

  const [showPrefilledPatientsList, setShowPrefilledPatientsList] =
    useState(false);

  const [hasDismissedCalendarStep, setHasDismissedCalendarStep] =
    useLocalStorageValue<boolean>(
      HAS_DISMISSED_CALENDAR_SETUP,
      authedProviderUser.email,
    );

  const [hasDismissedScheduleStep, setHasDismissedScheduleStep] =
    useLocalStorageValue<boolean>(
      HAS_DISMISSED_SCHEDULE_SETUP,
      authedProviderUser.email,
    );

  const [hasDismissedPaymentsStep, setHasDismissedPaymentsStep] =
    useLocalStorageValue<boolean>(
      HAS_DISMISSED_PAYMENTS_SETUP,
      authedProviderUser.email,
    );

  const [hasDismissedCalendlyClientsStep, setHasDismissedCalendlyClientsStep] =
    useLocalStorageValue<boolean>(
      HAS_DISMISSED_ADD_CALENDLY_CLIENTS_SETUP,
      authedProviderUser.email,
    );

  const [hasDismissedStorefrontStep, setHasDismissedStorefrontStep] =
    useLocalStorageValue<boolean>(
      HAS_DISMISSED_STOREFRONT_SETUP,
      authedProviderUser.email,
    );

  const [hasDismissedServiceStep, setHasDismissedServiceStep] =
    useLocalStorageValue<boolean>(
      HAS_DISMISSED_STOREFRONT_SETUP,
      authedProviderUser.email,
    );

  const [hasDismissedAppearanceStep, setHasDismissedAppearanceStep] =
    useLocalStorageValue<boolean>(
      HAS_DISMISSED_APPEARANCE_SETUP,
      authedProviderUser.email,
    );

  const showCalendarSetup =
    authedProviderUser &&
    isOwnerOrAdmin &&
    !hasNylasIntegration &&
    isCalendarSyncEnabled &&
    !hasDismissedCalendarStep;

  const showSchedulingSetup =
    authedProviderUser &&
    isOwnerOrAdmin &&
    !hasCalendlyIntegration &&
    !hasDismissedScheduleStep;

  const showPaymentsSetup =
    authedProviderUser &&
    isOwnerOrAdmin &&
    !canUseStripe &&
    !hasDismissedPaymentsStep;

  const showCalendlyClientsSetup = Boolean(
    authedProviderUser &&
      !hasCalendlyIntegration &&
      !hasDismissedCalendlyClientsStep,
  );

  const showSetupMyPage = Boolean(
    authedProviderUser &&
      isOwnerOrAdmin &&
      !authedProviderUser.storefrontSlug &&
      !hasDismissedStorefrontStep,
  );

  const showSetupAService = Boolean(
    authedProviderUser &&
      isOwnerOrAdmin &&
      !authedProviderUser.hasCreatedAProviderService &&
      !hasDismissedServiceStep,
  );

  const hasDefaultAppearance =
    (authedProviderUser?.primaryColor === DEFAULT_COLORS.PRIMARY ||
      !authedProviderUser?.primaryColor) &&
    (authedProviderUser?.secondaryColor === DEFAULT_COLORS.SECONDARY ||
      !authedProviderUser?.secondaryColor) &&
    authedProviderUser?.secondaryColor === DEFAULT_COLORS.SECONDARY &&
    !authedProviderUser?.squareLogoMedia?.url;

  const showSetupMyAppearance = Boolean(
    authedProviderUser &&
      isOwnerOrAdmin &&
      hasDefaultAppearance &&
      !hasDismissedAppearanceStep,
  );

  const onboardingSteps: QuickActionProps[] = useMemo(
    () => [
      ...(showCalendarSetup
        ? [
            {
              title: 'Sync your calendar',
              description: 'Connect your Google calendar with Homecoming',
              IconComponent: IllustrationCalendar,
              iconClassName: 'text-blue-125',
              buttonTitle: 'Connect calendar',
              onClick: () => {
                navigate('/integrations/calendar');
              },
              onDismiss: () => setHasDismissedCalendarStep(true),
            },
          ]
        : []),
      ...(showSchedulingSetup
        ? [
            {
              title: 'Set up client bookings',
              description:
                'Connect your Calendly account to allow clients to book appointments',
              IconComponent: IllustrationCalendar,
              iconClassName: 'text-blue-100',
              buttonTitle: 'Connect Calendly',
              onClick: () => {
                navigate('/integrations/calendly');
              },
              onDismiss: () => setHasDismissedScheduleStep(true),
            },
          ]
        : []),
      ...(showPaymentsSetup
        ? [
            {
              title: 'Set up payments',
              description: 'Connect your Stripe account with Homecoming',
              IconComponent: IllustrationBanknotes,
              iconClassName: 'text-green-100',
              buttonTitle: 'Connect Stripe',
              onClick: () => {
                navigate('/integrations/stripe');
              },
              onDismiss: () => setHasDismissedPaymentsStep(true),
            },
          ]
        : []),
      ...(showSetupMyAppearance
        ? [
            {
              title: 'Change the appearance of Homecoming',
              description:
                'Customize the Homecoming experience for you and your clients',
              IconComponent: IllustrationPencil,
              iconClassName: 'text-purple-100',
              buttonTitle: 'Change appearance',
              onClick: () => {
                navigate('/settings/appearance');
              },
              onDismiss: () => setHasDismissedAppearanceStep(true),
            },
          ]
        : []),
      ...(showCalendlyClientsSetup
        ? [
            {
              title: 'Import clients',
              description:
                'Some clients from Calendly are not on Homecoming, would you like to add them?',
              IconComponent: IllustrationFaces,
              iconClassName: 'text-green-125',
              onClick: () => {
                setShowPrefilledPatientsList(true);
                addPatients();
              },
              onDismiss: () => setHasDismissedCalendlyClientsStep(true),
            },
          ]
        : []),
      ...(showSetupMyPage
        ? [
            {
              title: 'Create your Homecoming page',
              description:
                'Customize your webpage and start accepting new clients',
              IconComponent: IllustrationSwirls,
              iconClassName: 'text-primary-150',
              buttonTitle: 'Set up webpage',
              onClick: () => {
                navigate('/your-page');
              },
              onDismiss: () => setHasDismissedStorefrontStep(true),
            },
          ]
        : []),
      ...(showSetupAService
        ? [
            {
              title: 'Create a service page',
              description:
                'Intake clients with forms, session bookings, payments, and more.',
              IconComponent: IllustrationBook,
              iconClassName: 'text-secondary-125',
              buttonTitle: 'Create your first service',
              onClick: () => {
                navigate('/services/new', { state: { from: '/home' } });
              },
              onDismiss: () => setHasDismissedServiceStep(true),
            },
          ]
        : []),
    ],
    [
      uniqueInvitees,
      showPaymentsSetup,
      showCalendarSetup,
      authedProviderUser,
      showSetupMyPage,
      showSetupAService,
      showSetupMyAppearance,
      showSchedulingSetup,
      showCalendlyClientsSetup,
    ],
  );

  const addPatients = () => {
    if (willHitClientLimit()) {
      showUpgradeModal(AnalyticsPage.Home);
      return;
    }
    setIsAddPatientsModalOpen(true);
  };

  const DAYS_TO_KEEP = 30; // Show last 30 days of notifications
  const MAX_NOTIFICATIONS = 100; // Limit total number shown
  // Update the notifications filter
  const notifications = useMemo(() => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - DAYS_TO_KEEP);

    return (
      authedProviderUser?.notifications?.filter((notification) => {
        if (notification.readAt && notification.archivedNotification) {
          return false;
        }
        const notificationDate = new Date(notification.createdAt || new Date());
        return notificationDate >= cutoffDate;
      }) ?? []
    )
      .sort((a, b) => {
        // Sort by date, newest first
        return (
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      })
      .slice(0, MAX_NOTIFICATIONS);
  }, [authedProviderUser?.notifications]);

  // Refresh notifications when the page is loaded
  useEffect(() => {
    refreshAuthedProviderUserNotifications();
  }, []);

  const [isSelectClientModalOpen, setSelectClientModalOpen] = useState(false);
  const [isAddPatientsModalOpen, setIsAddPatientsModalOpen] = useState(false);

  const [isAddPDFModalOpen, setIsAddPDFModalOpen] = useState(false);
  const [isAddLinkModalOpen, setIsAddLinkModalOpen] = useState(false);
  const [isAddAudioModalOpen, setIsAddAudioModalOpen] = useState(false);
  const [isAddImageModalOpen, setIsAddImageModalOpen] = useState(false);
  const [isAddOrEditTextModalOpen, setIsAddOrEditTextModalOpen] =
    useState(false);

  const openAddOrEditTextModal = () => setIsAddOrEditTextModalOpen(true);

  const navigateToLibrary = () => {
    return async () => {
      navigate('/library');
    };
  };

  const {
    data: meProviderProgramTagsData,
    refetch: refetchMeProviderProgramTags,
  } = useMeProviderProgramTagsWithCountQuery();

  const providerProgramTags =
    meProviderProgramTagsData?.meProvider?.provider?.programTags ?? [];

  const allNotifications = [...onboardingSteps, ...notifications];

  const isLatestActivityView = viewMode === ViewMode.LatestActivity;
  const isUnsignedFormsView = viewMode === ViewMode.UnsignedForms;

  const hasUnsignedForms = !isUnsignedFormsView && unsignedFormsCount > 0;

  return (
    <div className="md:h-[calc(100vh-var(--top-nav-height))] md:overflow-y-scroll">
      <HomeIllustration className="l-0 z-1 fixed h-screen text-neutral-30" />
      <PageContainer
        noPadding
        containerClassName="md:px-20 px-6 py-8 relative z-2"
      >
        <>
          <div className="mb-10 flex flex-row gap-x-3">
            <Button
              title="Add clients"
              size="small"
              IconComponent={UserIcon}
              theme="secondary-white"
              iconPosition="left"
              className="shadow-lg shadow-neutral-50"
              onClick={addPatients}
            />
            {authedProviderUser?.patientDataPermission !==
              PatientDataPermission.None && (
              <>
                <Button
                  title="Send Mail"
                  size="small"
                  IconComponent={MailIcon}
                  theme="secondary-white"
                  iconPosition="left"
                  className="!text-purple-100 shadow-lg shadow-neutral-50"
                  onClick={() => navigate('/follow-ups/new')}
                />
                {isMailSequencesEnabled && (
                  <Button
                    title="New Mail sequence"
                    size="small"
                    IconComponent={CalendarIcon}
                    theme="secondary-white"
                    iconPosition="left"
                    className="!text-secondary-100 shadow-lg shadow-neutral-50"
                    onClick={() => navigate(SCHEDULE_MAIL_PATH)}
                  />
                )}
                <Button
                  title="Start chat"
                  size="small"
                  IconComponent={ChatIcon}
                  theme="secondary-white"
                  iconPosition="left"
                  className="!text-blue-100 shadow-lg shadow-neutral-50"
                  onClick={() => setSelectClientModalOpen(true)}
                />
              </>
            )}
            <AddToLibraryMenu
              buttonClassName="!text-orange-125/80 shadow-lg shadow-neutral-50"
              buttonTheme="secondary-white"
              buttonSize="small"
              onClickAddLink={() => setIsAddLinkModalOpen(true)}
              onClickAddImage={() => setIsAddImageModalOpen(true)}
              onClickAddPDF={() => setIsAddPDFModalOpen(true)}
              onClickAddAudio={() => setIsAddAudioModalOpen(true)}
              onClickAddForm={(formType) =>
                navigate('/library/forms/new', {
                  state: {
                    formType,
                    fromPage: 'home',
                  },
                })
              }
              onClickAddText={openAddOrEditTextModal}
              IconComponent={PlusSmIcon}
            />
            {canUseStripe && (
              <Button
                title="Invoice"
                size="small"
                IconComponent={BanknotesIcon}
                theme="secondary-white"
                iconPosition="left"
                className="!text-green-100 shadow-lg shadow-neutral-50"
                onClick={() => navigate('/payments/new')}
              />
            )}
          </div>
          <div className="flex flex-col gap-x-24 pl-2 lg:flex-row">
            {hasCalendlyIntegration && (
              <div className="mb-6 flex w-full flex-col items-start justify-start lg:w-[300px]">
                <Transition
                  show={true}
                  appear={true}
                  {...defaultTransitionProps}
                  style={{ transitionDelay: '0ms' }}
                >
                  <div className="text-subtitle-small">Up next</div>
                </Transition>
                {showCalendlyReconnectWarning && (
                  <div className="mt-3 w-full">
                    <Alert
                      level="warning"
                      message="Reconnect Calendly to see upcoming events"
                    >
                      <p className="mt-1">
                        Your Calendly integration needs attention. Go to{' '}
                        <Link
                          to="/integrations/calendly"
                          className="text-blue-100 underline hover:text-blue-125"
                        >
                          Integrations → Calendly
                        </Link>{' '}
                        to reconnect.
                      </p>
                    </Alert>
                  </div>
                )}
                {authedProviderUser &&
                  isOwnerOrAdmin &&
                  isCurrentUserInCalendlyTeam &&
                  teamProviderUsers.length > 0 && (
                    <Transition
                      show={true}
                      appear={true}
                      {...defaultTransitionProps}
                      style={{ transitionDelay: '50ms' }}
                    >
                      <div className="mt-4 w-[354px]">
                        <label className="mb-1 block text-caption text-secondary-125">
                          Showing Calendly events for:
                        </label>
                        <Combobox
                          as="div"
                          className="relative w-full text-body"
                          value={
                            teamProviderUsers.find(
                              (u) => u.id === selectedProviderUserId,
                            ) || null
                          }
                          onChange={(
                            user: typeof teamProviderUsers[0] | null,
                          ) => {
                            if (user) {
                              setSelectedProviderUserId(user.id);
                            }
                          }}
                        >
                          <div className="relative">
                            <Combobox.Button className="relative flex w-full cursor-default flex-row items-center rounded-md border border-neutral-75 bg-white py-3 pl-4 pr-9 text-left text-body text-green-150 shadow-sm focus:outline-none focus:ring-2 focus:ring-secondary-50">
                              {selectedProviderUserId ? (
                                (() => {
                                  const selectedUser = teamProviderUsers.find(
                                    (u) => u.id === selectedProviderUserId,
                                  );
                                  if (!selectedUser) return null;
                                  const displayName =
                                    selectedUser.name || selectedUser.email;
                                  return (
                                    <div className="flex items-center gap-3 truncate">
                                      <Avatar
                                        size="small"
                                        name={displayName}
                                        imageUrl={
                                          selectedUser.profileImageUrl ||
                                          undefined
                                        }
                                      />
                                      <div className="flex flex-col truncate">
                                        <span className="truncate">
                                          {displayName}
                                          {selectedUser.id ===
                                          authedProviderUser.id
                                            ? ' (You)'
                                            : ''}
                                        </span>
                                        <span className="text-small-caption text-neutral-110">
                                          {selectedUser.email}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })()
                              ) : (
                                <span className="text-neutral-125/75">
                                  Select provider user
                                </span>
                              )}
                            </Combobox.Button>
                            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-3">
                              <ChevronDownIcon
                                className="h-5 w-5 text-neutral-125"
                                aria-hidden="true"
                              />
                            </Combobox.Button>
                          </div>
                          <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                          >
                            <Combobox.Options className="absolute left-0 right-0 z-10 mt-2 max-h-[320px] w-full divide-y divide-neutral-75 overflow-auto rounded-lg border border-neutral-75 bg-white py-1 text-caption shadow-lg focus:outline-none focus:ring-0">
                              {teamProviderUsers.length === 0 ? (
                                <div className="py-3 px-5 text-center text-neutral-110">
                                  No team members found.
                                </div>
                              ) : (
                                teamProviderUsers.map((user) => {
                                  const displayName = user.name || user.email;
                                  return (
                                    <Combobox.Option key={user.id} value={user}>
                                      {({ active, selected }) => (
                                        <div
                                          className={`relative flex cursor-pointer select-none flex-row items-center truncate py-3 px-5 text-green-150 hover:bg-neutral-25 ${
                                            active || selected
                                              ? 'bg-neutral-25'
                                              : ''
                                          }`}
                                        >
                                          <Avatar
                                            size="small"
                                            name={displayName}
                                            imageUrl={
                                              user.profileImageUrl || undefined
                                            }
                                            className="mr-3"
                                          />
                                          <div className="flex flex-col">
                                            <span>
                                              {displayName}
                                              {user.id === authedProviderUser.id
                                                ? ' (You)'
                                                : ''}
                                            </span>
                                            <span className="text-small-caption text-neutral-110">
                                              {user.email}
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                    </Combobox.Option>
                                  );
                                })
                              )}
                            </Combobox.Options>
                          </Transition>
                        </Combobox>
                      </div>
                    </Transition>
                  )}
                {isUpNextLoading ? (
                  <div className="mt-6 flex w-full items-center justify-center gap-2 text-neutral-125">
                    <Spinner className="h-5 w-5" />
                    Loading...
                  </div>
                ) : (
                  <Transition
                    as="div"
                    show={Boolean(upcomingEventsData?.scheduledEvents)}
                    {...defaultTransitionProps}
                    className="mt-6 flex flex-wrap items-start gap-6 sm:flex-row lg:flex-col lg:flex-nowrap lg:gap-y-5"
                    style={{ transitionDelay: '100ms' }}
                  >
                    <>
                      {hasUpcomingEvents ? (
                        <>
                          {upcomingEvents?.map((upcomingEvent, index) => (
                            <Transition
                              key={index}
                              show={true}
                              {...defaultTransitionProps}
                              appear={true}
                              style={{
                                transitionDelay: `${100 + index * 50}ms`,
                              }}
                            >
                              <UpcomingEvent
                                upcomingEvent={upcomingEvent}
                                isDemo={!hasCalendlyIntegration}
                              />
                            </Transition>
                          ))}
                        </>
                      ) : (
                        <div className="text-neutral-125">
                          No upcoming sessions
                        </div>
                      )}
                    </>
                  </Transition>
                )}
              </div>
            )}
            <div className="flex-1">
              <Transition
                show={true}
                appear={true}
                {...defaultTransitionProps}
                style={{
                  transitionDelay: hasCalendlyIntegration ? '50ms' : '0ms',
                }}
              >
                <div className="mb-6 flex h-[30px] w-full flex-row items-center justify-between">
                  <div className="font-sans text-subtitle-small">
                    {isLatestActivityView && 'Latest activity'}
                    {isUnsignedFormsView && 'Outstanding forms'}
                  </div>
                  <div className="flex space-x-2">
                    {!isLatestActivityView && (
                      <Button
                        title="Return to unreads"
                        IconComponent={InboxIcon}
                        theme="secondary-white"
                        iconPosition="left"
                        size="small"
                        className="!text-neutral-125 shadow-lg shadow-neutral-50"
                        onClick={() =>
                          setViewModeAndParams(ViewMode.LatestActivity)
                        }
                      />
                    )}
                    {hasUnsignedForms ? (
                      <Button
                        title={`Complete ${unsignedFormsCount} outstanding ${
                          unsignedFormsCount === 1 ? 'form' : 'forms'
                        }`}
                        IconComponent={ActivityForm}
                        theme="primary"
                        iconPosition="left"
                        size="small"
                        className="!border-orange-100 !bg-orange-25 !text-orange-150 shadow-lg shadow-neutral-50 hover:!bg-orange-50"
                        onClick={() =>
                          setViewModeAndParams(ViewMode.UnsignedForms)
                        }
                      />
                    ) : isLatestActivityView ? (
                      <Button
                        title="No pending actions"
                        IconComponent={CircleGreenCheck}
                        theme="secondary"
                        iconPosition="left"
                        size="small"
                        className="!cursor-default !border-green-100 !bg-green-20 !text-green-150 shadow-lg shadow-neutral-50 hover:!bg-green-20 disabled:!bg-green-20 disabled:!text-green-150/70"
                        disabled={true}
                        onClick={() => {
                          // No action needed for disabled state
                        }}
                      />
                    ) : null}
                    {hasPendingClientSessions && (
                      <Button
                        title={`Review ${pendingClientSessionsCount} client ${
                          pendingClientSessionsCount === 1
                            ? 'session'
                            : 'sessions'
                        }`}
                        IconComponent={BellBadgeCalendar}
                        theme="secondary-white"
                        iconPosition="left"
                        size="small"
                        className="!text-neutral-125 shadow-lg shadow-neutral-50"
                        onClick={() => navigate('/client_sessions/pending')}
                      />
                    )}
                  </div>
                </div>
              </Transition>

              <Transition
                show={true}
                appear={true}
                {...defaultTransitionProps}
                style={{
                  transitionDelay: hasCalendlyIntegration ? '150ms' : '100ms',
                }}
              >
                <div className="rounded-lg bg-white p-7 shadow-lg shadow-neutral-30">
                  {isLatestActivityView && (
                    <InboxView
                      allNotifications={allNotifications}
                      initialDelay={hasCalendlyIntegration ? 200 : 150}
                      providerPrimaryColor={authedProviderUser?.primaryColor}
                    />
                  )}
                  {isUnsignedFormsView && <UnsignedFormsView />}
                </div>
              </Transition>
            </div>
          </div>
        </>

        <SelectPatientModal
          isOpen={isSelectClientModalOpen}
          setClosed={() => setSelectClientModalOpen(false)}
        />
        <AddLinkModal
          isModalOpen={isAddLinkModalOpen}
          setClosed={() => setIsAddLinkModalOpen(false)}
          onAdded={navigateToLibrary}
        />
        <AddPDFModal
          isModalOpen={isAddPDFModalOpen}
          setClosed={() => setIsAddPDFModalOpen(false)}
          onAdded={navigateToLibrary}
        />
        <AddImageModal
          isModalOpen={isAddImageModalOpen}
          setClosed={() => setIsAddImageModalOpen(false)}
          onAdded={navigateToLibrary}
        />
        <AddAudioModal
          isModalOpen={isAddAudioModalOpen}
          setClosed={() => setIsAddAudioModalOpen(false)}
          onAdded={navigateToLibrary}
        />
        <AddOrEditTextModal
          isModalOpen={isAddOrEditTextModalOpen}
          setClosed={() => setIsAddOrEditTextModalOpen(false)}
          onAdded={navigateToLibrary}
        />
        <AddPatientsModal
          isClientsPage={false}
          isModalOpen={isAddPatientsModalOpen}
          setClosed={() => setIsAddPatientsModalOpen(false)}
          providerProgramTags={providerProgramTags}
          refetchMeProviderProgramTags={refetchMeProviderProgramTags}
          {...(showPrefilledPatientsList && {
            prefilledPatientsList: uniqueInvitees,
            isFromCalendly: true,
          })}
        />
      </PageContainer>
    </div>
  );
};

export default Home;
